import '@/css/baseline.css';
import '@/css/global.css';
import '@/css/root.css';
import {
  type PaletteMode,
  responsiveFontSizes as responsiveFontSizesMui,
  StyledEngineProvider,
  ThemeProvider,
  // oxlint-disable-next-line no-restricted-imports
  useTheme,
} from '@mui/material';
import { useLayoutEffect, type ReactElement, type ReactNode } from 'react';
import {
  // oxlint-disable-next-line no-restricted-imports
  ErrorBoundary,
  type FallbackProps,
} from 'react-error-boundary';
import { EnsureCookieSupport } from '@/components/Common/EnsureCookieSupport';
import { AppRootErrorFallback } from '@/components/Error/AppRootErrorFallback';
import { SettingsProvider } from '@/contexts/settings-context';
import { createTheme } from '@/theme';
import styles from '@/css/components/AppShell.module.css';

export const AppShell = ({ children, paletteMode }: { children: ReactNode; paletteMode?: PaletteMode }) => (
  <ErrorBoundary FallbackComponent={CatastrophicErrorFallback}>
    <EnsureCookieSupport>
      <SettingsProvider>
        {({ direction, ...props }) => {
          let theme = createTheme({ direction, paletteMode });
          if (props.responsiveFontSizes) {
            theme = responsiveFontSizesMui(theme);
          }
          return (
            <StyledEngineProvider injectFirst={true}>
              <ThemeProvider theme={theme}>
                <BodyTheme>
                  <ErrorBoundary FallbackComponent={AppRootErrorFallback}>{children}</ErrorBoundary>
                </BodyTheme>
              </ThemeProvider>
            </StyledEngineProvider>
          );
        }}
      </SettingsProvider>
    </EnsureCookieSupport>
  </ErrorBoundary>
);

function CatastrophicErrorFallback({ error }: FallbackProps) {
  return (
    <div className={styles.catastrophicErrorFallback}>
      <h3>Oops, something went wrong</h3>
      <p>{String(error)}</p>
      <p style={{ marginTop: 0 }}>We've logged the error, and our crew is on the case.</p>
      <a href="https://status.qdrant.io" target="_blank" rel="noopener noreferrer">
        Qdrant status page
      </a>
    </div>
  );
}

function BodyTheme({ children }: { children: ReactElement }) {
  const theme = useTheme();

  useLayoutEffect(() => {
    const body = document.querySelector('body');
    if (body) {
      body.style.setProperty('--mui-palette-text-primary', theme.palette.text.primary);
      body.style.setProperty('--mui-palette-background-default', theme.palette.background.default);
    }
  }, [theme.palette]);

  return children;
}
